/**
 * Security Page
 * Factual description of how statement data is handled, stored, and deleted.
 * Every claim on this page must be backed by code or infrastructure defaults;
 * do not add certifications, SLAs, or capabilities that are not implemented.
 */

import { Helmet } from 'react-helmet-async'

export function SecurityPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Security — BankStatementReader</title>
        <meta name="description" content="How BankStatementReader handles your statements: browser-only free tools, encrypted storage, third-party AI extraction disclosure, and deletion you control." />
        <link rel="canonical" href="https://bankstatementreader.com/security" />
      </Helmet>
      <div className="max-w-4xl mx-auto p-8 py-16">
        <h1 className="text-4xl font-bold mb-8 text-gray-900">Security</h1>

        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          <section>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Last Updated:</strong> July 2, 2026
            </p>
            <p className="text-gray-700 leading-relaxed">
              You are trusting us with financial documents, so this page describes plainly how they are
              handled. It only lists what the product actually does. If anything here is unclear,
              email us before uploading anything.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">We never ask for your bank login</h2>
            <p className="text-gray-700 leading-relaxed">
              BankStatementReader works on PDF statements you already have. We do not connect to your
              bank, and we will never ask for your online banking credentials. If a message asking for
              bank credentials appears to come from us, it is not from us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">The free tools run in your browser</h2>
            <p className="mb-3 text-gray-700 leading-relaxed">
              The free tools on this site (the converter demo, statement redaction, CSV utilities, and
              the rest) process files entirely in your browser using client-side JavaScript.
            </p>
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <ul className="list-disc list-inside space-y-1 text-green-800">
                <li>Files you open in the free tools are not uploaded to our servers</li>
                <li>The only network requests those pages make are for the page itself and a built-in sample dataset</li>
                <li>Closing the tab discards everything</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">What happens when you upload a statement</h2>
            <p className="mb-3 text-gray-700 leading-relaxed">
              The full converter (for signed-in users) works differently, because extraction runs on our
              infrastructure:
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded p-4">
              <ul className="list-disc list-inside space-y-1 text-indigo-800">
                <li>Your PDF is transmitted over HTTPS and stored in our cloud object storage, which encrypts data at rest</li>
                <li>Processing systems access the file through short-lived signed URLs, not shared credentials</li>
                <li>Statement page images are sent to a third-party AI provider solely to perform extraction, as described in our Privacy Policy</li>
                <li>Temporary copies made during processing are deleted when processing finishes</li>
                <li>Extracted transactions are stored in our database and are accessible only to your account</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Deletion you control</h2>
            <p className="mb-3 text-gray-700 leading-relaxed">
              Statements are retained until you delete them. There is no forced retention.
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Deleting a statement from your dashboard removes the uploaded PDF, the extracted transactions, and any generated export files</li>
              <li>Deleting your account removes your account data and purges your stored statements and exports</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Payments</h2>
            <p className="text-gray-700 leading-relaxed">
              Payments are handled by Stripe using Stripe-hosted checkout. Your card number never
              touches our servers, and our database stores only Stripe customer and subscription
              references, never card details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Reporting a security issue</h2>
            <p className="text-gray-700 leading-relaxed">
              If you believe you have found a vulnerability, email{' '}
              <a href="mailto:support@bankstatementreader.com" className="text-indigo-600 hover:text-indigo-800">
                support@bankstatementreader.com
              </a>{' '}
              with the details. We read every report.
            </p>
          </section>

          <div className="pt-4 border-t border-gray-200">
            <a href="/" className="text-indigo-600 hover:text-indigo-800 font-medium">
              ← Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
